import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle2, AlertTriangle, Layers, FileText } from 'lucide-react';
import apiClient from '../../lib/apiClient';

/**
 * POComparisonView — side-by-side comparison of list scrape vs detail scrape data.
 *
 * Shows what additional data the detail scrape provides over the list scrape,
 * including line items, UNSPSC codes, and spend breakdowns.
 *
 * Usage: <POComparisonView documentNumber="0000080450" />
 */
function POComparisonView({ documentNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentNumber) return;
    setLoading(true);
    apiClient.get(`/api/scraper/po/${documentNumber}/comparison`)
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [documentNumber]);

  if (loading) return <div className="poc-loading">Loading comparison...</div>;
  if (error) return <div className="poc-error">Error: {error}</div>;
  if (!data) return null;

  const list = data.listData?.[0] || {};
  const detail = data.detailHeader || {};
  const lines = data.detailLines || [];
  const analysis = data.analysis || {};

  const fields = [
    { label: 'Department', list: list.department_name, detail: detail.department_name },
    { label: 'Supplier', list: list.supplier_name, detail: detail.supplier_name },
    { label: 'Description', list: list.item_description, detail: lines.length > 0 ? `${lines.length} line items (see below)` : '—', isDescription: true },
    { label: 'Grand Total', list: list.line_total ? `$${Number(list.line_total).toLocaleString()}` : '—', detail: detail.grand_total ? `$${Number(detail.grand_total).toLocaleString()}` : '—' },
    { label: 'Merchandise', list: null, detail: detail.merchandise_amount ? `$${Number(detail.merchandise_amount).toLocaleString()}` : null, detailOnly: true },
    { label: 'Freight/Tax', list: null, detail: detail.freight_tax_misc ? `$${Number(detail.freight_tax_misc).toLocaleString()}` : null, detailOnly: true },
    { label: 'Start Date', list: list.start_date, detail: detail.start_date },
    { label: 'End Date', list: list.end_date, detail: detail.end_date },
    { label: 'Status', list: list.po_status, detail: detail.transaction_status },
    { label: 'Acq Type', list: list.acquisition_type, detail: detail.acquisition_type },
    { label: 'Acq Method', list: list.acquisition_method, detail: detail.acquisition_method },
    { label: 'Buyer', list: list.buyer, detail: detail.buyer_name },
    { label: 'Buyer Email', list: list.buyer_email, detail: detail.buyer_email },
    { label: 'LPA Contract', list: list.lpa_contract_id, detail: detail.lpa_contract_id },
    { label: 'Bill Code', list: null, detail: detail.bill_code || null, detailOnly: true },
    { label: 'UNSPSC', list: list.unspsc_code, detail: analysis.newUnspscCodes?.[0] || null },
    { label: 'Classification', list: list.classification_method || '—', detail: null, listOnly: true },
    { label: 'Component', list: list.component_name || '—', detail: null, listOnly: true },
  ];

  const detailOnlyCount = fields.filter(f => f.detailOnly && f.detail).length;

  return (
    <div className="poc-container">
      <div className="poc-header">
        <FileText size={16} />
        <h3>List vs Detail Comparison — PO {documentNumber}</h3>
        {onClose && <button className="poc-close" onClick={onClose}>&times;</button>}
      </div>

      {/* Value-add banner */}
      {analysis.hasDetailData && (
        <div className={`poc-banner ${analysis.detailAddsValue ? 'poc-banner-positive' : 'poc-banner-neutral'}`}>
          {analysis.detailAddsValue ? (
            <>
              <CheckCircle2 size={14} />
              <span>Detail scrape adds <strong>{detailOnlyCount} fields</strong> and <strong>{lines.length} line items</strong> not available from list scrape</span>
            </>
          ) : (
            <>
              <AlertTriangle size={14} />
              <span>Detail scrape data matches list scrape — no additional fields found</span>
            </>
          )}
        </div>
      )}

      {!analysis.hasDetailData && (
        <div className="poc-banner poc-banner-warning">
          <AlertTriangle size={14} />
          <span>No detail scrape data available for this PO. Queue it for detail extraction.</span>
        </div>
      )}

      {/* Side-by-side table */}
      <div className="poc-table-wrap">
        <table className="poc-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>List Scrape</th>
              <th></th>
              <th>Detail Scrape</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => {
              const listVal = f.list || (f.listOnly ? null : '—');
              const detailVal = f.detail || (f.detailOnly ? null : '—');
              const isNew = f.detailOnly && f.detail;
              return (
                <tr key={i} className={isNew ? 'poc-row-new' : ''}>
                  <td className="poc-field-label">{f.label}</td>
                  <td className={`poc-field-value ${f.listOnly ? 'poc-field-only' : ''}`}>
                    {f.isDescription ? (
                      <span className="poc-desc" title={listVal}>{(listVal || '').substring(0, 60)}{listVal?.length > 60 ? '...' : ''}</span>
                    ) : (
                      listVal || <span className="poc-na">—</span>
                    )}
                  </td>
                  <td className="poc-arrow">
                    {isNew && <ArrowRight size={12} className="poc-arrow-new" />}
                  </td>
                  <td className={`poc-field-value ${isNew ? 'poc-field-new' : ''} ${f.detailOnly && !f.detail ? 'poc-field-empty' : ''}`}>
                    {detailVal || <span className="poc-na">—</span>}
                    {isNew && <span className="poc-badge-new">NEW</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Line items (detail-only) */}
      {lines.length > 0 && (
        <div className="poc-lines">
          <div className="poc-lines-header">
            <Layers size={14} />
            <span>Line Items ({lines.length}) — Detail Scrape Only</span>
          </div>
          <table className="poc-lines-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>UNSPSC</th>
                <th>UOM</th>
                <th>Qty</th>
                <th>Unit $</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((li, i) => (
                <tr key={i}>
                  <td>{li.line_number}</td>
                  <td className="poc-line-desc" title={li.item_description}>{(li.item_description || '').substring(0, 60)}{li.item_description?.length > 60 ? '...' : ''}</td>
                  <td className="poc-line-unspsc">{li.unspsc_code || '—'}</td>
                  <td>{li.purchase_order_uom || '—'}</td>
                  <td className="poc-line-num">{li.quantity || '—'}</td>
                  <td className="poc-line-num">{li.unit_price ? `$${Number(li.unit_price).toLocaleString()}` : '—'}</td>
                  <td className="poc-line-num">{li.line_total ? `$${Number(li.line_total).toLocaleString()}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Spend breakdown (detail-only) */}
      {analysis.spendBreakdown && (
        <div className="poc-spend">
          <div className="poc-spend-header">Spend Breakdown (Detail Only)</div>
          <div className="poc-spend-grid">
            <div className="poc-spend-item">
              <span className="poc-spend-label">Merchandise</span>
              <span className="poc-spend-value">${Number(analysis.spendBreakdown.merchandise || 0).toLocaleString()}</span>
            </div>
            <div className="poc-spend-item">
              <span className="poc-spend-label">Freight/Tax/Misc</span>
              <span className="poc-spend-value">${Number(analysis.spendBreakdown.freightTaxMisc || 0).toLocaleString()}</span>
            </div>
            <div className="poc-spend-item poc-spend-total">
              <span className="poc-spend-label">Grand Total</span>
              <span className="poc-spend-value">${Number(analysis.spendBreakdown.grandTotal || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default POComparisonView;
